import React, { useState, useEffect } from 'react';
import { HandHeart, Send, Heart, ShieldCheck } from 'lucide-react';
import { HERO_IMAGES } from '../mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

const CATEGORIES = ['Salvation', 'Healing', 'Family', 'Provision', 'Guidance', 'Thanksgiving', 'Other'];

export default function Prayer() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('Healing');
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [wall, setWall] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.listPrayers().then(setWall).catch(() => setWall([]));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast({ title: 'A prayer needs words', description: 'Please share what we can pray about.' });
      return;
    }
    setBusy(true);
    try {
      await api.createPrayer({ name, email, category, message, anonymous });
      setName(''); setEmail(''); setMessage(''); setAnonymous(false);
      await refresh();
      toast({ title: 'We are praying with you', description: 'Your request has been received. Grace be upon you.' });
    } catch (err) {
      toast({ title: 'Could not submit', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  const prayFor = async (id) => {
    try { await api.prayFor(id); await refresh(); } catch (_) {}
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.prayingHands} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/50" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Join Us In Prayer</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">We would love<br />to pray with you.</h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl">
            Whatever weight you carry today, you do not carry it alone. Share your prayer request and we will lift it faithfully before the throne of grace.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <Card className="lg:col-span-3 p-8 md:p-10 bg-white rounded-2xl border border-ink/5 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-full bg-cream flex items-center justify-center">
                <HandHeart className="h-5 w-5 text-teal-deep" />
              </div>
              <div>
                <h2 className="font-serif-display text-2xl text-ink">Submit a Prayer Request</h2>
                <p className="text-sm text-ink/60">All requests remain confidential.</p>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name">Name (optional)</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="email">Email (optional)</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="So we can follow up" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <RadioGroup value={category} onValueChange={setCategory} className="mt-2 flex flex-wrap gap-2">
                  {CATEGORIES.map(c => (
                    <label key={c} htmlFor={`c-${c}`} className={`cursor-pointer px-3 py-1.5 rounded-full text-sm border transition-colors ${category === c ? 'bg-teal-deep text-white border-teal-deep' : 'bg-white text-ink/70 border-ink/15 hover:border-teal-brand'}`}>
                      <RadioGroupItem id={`c-${c}`} value={c} className="sr-only" />
                      {c}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label htmlFor="message">Your Prayer Request</Label>
                <Textarea id="message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Share what is on your heart…" className="mt-1.5" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="anon" checked={anonymous} onCheckedChange={setAnonymous} />
                <Label htmlFor="anon" className="text-sm text-ink/70 cursor-pointer">Post anonymously to the prayer wall</Label>
              </div>
              <Button type="submit" className="w-full bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-12">
                <Send className="mr-2 h-4 w-4" /> Send Prayer Request
              </Button>
              <p className="flex items-center gap-2 text-xs text-ink/50 justify-center">
                <ShieldCheck className="h-3.5 w-3.5" /> Your details are safe. We do not share your information.
              </p>
            </form>
          </Card>

          {/* Prayer wall */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="h-5 w-5 text-gold-brand" />
              <h2 className="font-serif-display text-2xl text-ink">Prayer Wall</h2>
            </div>
            <p className="text-sm text-ink/60 mb-5">Stand in agreement with those praying. Tap the heart to pray.</p>
            <div className="space-y-4 max-h-[700px] overflow-y-auto pr-1">
              {wall.length === 0 && (
                <Card className="p-6 bg-cream border border-gold-brand/30">
                  <p className="text-ink/70 italic">The wall is quiet. Be the first to share a prayer today.</p>
                </Card>
              )}
              {wall.map(w => (
                <Card key={w.id} className="p-5 bg-white rounded-xl border border-ink/5 hover-lift">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs tracking-[0.2em] uppercase text-gold-deep font-medium">{w.category}</span>
                    <button onClick={() => prayFor(w.id)} className="flex items-center gap-1 text-xs text-ink/60 hover:text-gold-brand">
                      <Heart className={`h-4 w-4 ${w.prayers > 0 ? 'fill-gold-brand text-gold-brand' : ''}`} />
                      <span>{w.prayers}</span>
                    </button>
                  </div>
                  <p className="text-ink/80 leading-relaxed">{w.message}</p>
                  <p className="text-xs text-ink/50 mt-3">&mdash; {w.name}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
